<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">
          {{ pageTitle }}
          <button type="button" class="help-icon-btn" :class="{ active: showHelp }" @click="showHelp = !showHelp" title="查看設定說明">
            <i class="ri-question-line"></i>
          </button>
        </h1>
      </div>
      <div class="header-actions">
        <button type="button" class="btn btn-secondary" @click="handleCancel">
          <i class="ri-arrow-left-line"></i>
          返回列表
        </button>
        <button type="button" class="btn btn-secondary" @click="handleReset">重置</button>
        <button type="button" class="btn btn-primary" :disabled="submitting" @click="handleSubmit">
          <span v-if="submitting" class="spinner spinner-sm"></span>
          <span v-else>儲存設定</span>
        </button>
      </div>
    </div>

    <div class="page-container">

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="fetchRule">重試</button>
    </div>

    <div v-else class="card">
      <div class="card-body">
        <transition name="guide-fade">
          <div v-if="showHelp" class="setup-guide-alert">
            <i class="ri-information-line"></i>
            <div>
              <strong>時間區間給點規則設定說明</strong>
              <ol>
                <li>在「基本資訊」設定規則名稱、時間區間與狀態。</li>
                <li>設定各等級的活動回饋率（%），留空則沿用常態規則。</li>
                <li>在「給點計算規則」選擇沿用常態規則，或自訂基數、捨入與生效天數。</li>
                <li>完成設定後點擊「<strong>儲存設定</strong>」。</li>
                <li>活動期間內進行中的規則將取代常態給點規則。</li>
              </ol>
            </div>
            <button type="button" class="guide-close-btn" @click="showHelp = false">
              <i class="ri-close-line"></i>
            </button>
          </div>
        </transition>

        <div class="form-section basic-info-section">
          <h2 class="section-title">基本資訊</h2>

          <div class="form-group">
            <label class="form-label required">規則名稱</label>
            <input
              v-model="form.ruleName"
              type="text"
              class="form-control"
              :class="{ 'is-invalid': errors.ruleName }"
              placeholder="例如：週年慶雙倍點數"
              maxlength="200"
            />
            <div v-if="errors.ruleName" class="invalid-feedback">
              {{ errors.ruleName }}
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label class="form-label required">開始時間</label>
              <input
                v-model="form.startTime"
                type="datetime-local"
                class="form-control"
                :class="{ 'is-invalid': errors.startTime }"
              />
              <div v-if="errors.startTime" class="invalid-feedback">
                {{ errors.startTime }}
              </div>
            </div>

            <div class="form-group">
              <label class="form-label required">結束時間</label>
              <input
                v-model="form.endTime"
                type="datetime-local"
                class="form-control"
                :class="{ 'is-invalid': errors.endTime }"
              />
              <div v-if="errors.endTime" class="invalid-feedback">
                {{ errors.endTime }}
              </div>
            </div>
          </div>

          <div class="form-group">
            <label class="form-label">狀態</label>
            <select v-model="form.status" class="form-control">
              <option value="draft">草稿</option>
              <option value="scheduled">已排程</option>
              <option value="active">進行中</option>
              <option value="ended">已結束</option>
              <option value="disabled">已停用</option>
            </select>
            <small class="form-text">變更為進行中或已排程時，系統會檢查時間區間是否與其他規則重疊</small>
          </div>

          <div v-if="timeOverlapWarning" class="alert alert-warning">
            <i class="ri-alert-line"></i>
            {{ timeOverlapWarning }}
          </div>
        </div>

        <div class="tab-bar">
          <button
            type="button"
            class="tab-btn"
            :class="{ active: activeTab === 'tiers' }"
            @click="activeTab = 'tiers'"
          >
            <i class="ri-medal-line"></i>
            會員等級回饋率
          </button>
          <button
            type="button"
            class="tab-btn"
            :class="{ active: activeTab === 'settings' }"
            @click="activeTab = 'settings'"
          >
            <i class="ri-settings-3-line"></i>
            給點計算規則
          </button>
        </div>

        <form @submit.prevent="handleSubmit">
          <div v-show="activeTab === 'tiers'">
            <div class="quick-rule-summary">
              <div class="quick-rule-summary-title">
                <i class="ri-lightbulb-line"></i>
                目前給點計算規則
              </div>
              <div class="quick-rule-summary-items">
                <span class="calc-mode-badge" :class="form.useNormalCalculationSettings ? 'mode-normal' : 'mode-custom'">
                  {{ form.useNormalCalculationSettings ? '沿用常態規則' : '自訂規則' }}
                </span>
                <span>金額基數：每 {{ previewBaseAmount }} 元</span>
                <span>捨入方式：{{ roundingMethodLabel }}</span>
                <span>生效天數：{{ effectiveDaysDisplay }} 天</span>
                <span
                  class="effective-mode-badge"
                  :class="effectiveDaysDisplay > 0 ? 'effective-mode-delayed' : 'effective-mode-immediate'"
                >
                  {{ effectiveDaysDisplay > 0 ? '延後生效模式' : '立即生效' }}
                </span>
              </div>
              <button type="button" class="btn btn-sm btn-secondary" @click="activeTab = 'settings'">
                調整給點計算規則
              </button>
            </div>

            <div class="form-section">
              <h2 class="section-title">會員等級回饋率</h2>
              <p class="section-description">設定活動期間各等級回饋率；留空則沿用常態規則的回饋率</p>

              <div v-if="tiers.length === 0" class="alert alert-warning">
                <p>尚未設定會員等級，請先前往 <router-link to="/client/member-tiers">會員等級管理</router-link> 建立等級</p>
              </div>

              <div v-else class="tiers-layout">
                <div class="tiers-left">
                  <h3 class="sub-section-title">回饋率設定</h3>
                  <div class="tier-config-list">
                    <div v-for="tier in tiers" :key="tier.id" class="tier-config-item">
                      <div class="tier-info">
                        <span v-if="tier.color" class="tier-name" :style="{ color: tier.color }">
                          {{ tier.tierName }}
                        </span>
                        <span v-else class="tier-name">{{ tier.tierName }}</span>
                        <code class="tier-code">{{ tier.tierCode }}</code>
                      </div>
                      <div class="tier-input">
                        <div class="input-group">
                          <input
                            v-model.number="form.tierConfig[tier.tierCode]"
                            type="number"
                            class="form-control"
                            :class="{ 'is-invalid': errors[`tier_${tier.tierCode}`] }"
                            placeholder="留空使用常態規則"
                            min="0"
                            step="1"
                          />
                          <span class="input-group-text">%</span>
                        </div>
                        <div v-if="errors[`tier_${tier.tierCode}`]" class="invalid-feedback">
                          {{ errors[`tier_${tier.tierCode}`] }}
                        </div>
                        <small class="form-text">留空則使用常態規則的回饋率；可超過 100%，須為整數</small>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="tiers-right">
                  <h3 class="sub-section-title">計算預覽</h3>
                  <div class="preview-amount-row">
                    <label class="preview-amount-label">輸入消費金額</label>
                    <div class="input-group preview-amount-input">
                      <span class="input-group-text">NT$</span>
                      <input
                        v-model.number="previewAmount"
                        type="number"
                        class="form-control"
                        min="0"
                        step="1"
                        placeholder="100"
                      />
                    </div>
                    <small class="preview-settings-hint">
                      基數 {{ previewBaseAmount }} 元・{{ roundingMethodLabel }}
                    </small>
                  </div>

                  <div class="preview-table-wrap">
                    <table class="preview-table">
                      <thead>
                        <tr>
                          <th>等級</th>
                          <th>回饋率</th>
                          <th>單位數</th>
                          <th>點數</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="tier in tiers" :key="tier.id">
                          <td>
                            <span v-if="tier.color" :style="{ color: tier.color }" class="preview-tier-name">{{ tier.tierName }}</span>
                            <span v-else class="preview-tier-name">{{ tier.tierName }}</span>
                          </td>
                          <td>{{ displayTierRate(tier.tierCode) }}</td>
                          <td class="preview-units">{{ tierPreview(tier.tierCode).units }}</td>
                          <td class="preview-points">
                            <strong>{{ tierPreview(tier.tierCode).points }}</strong> 點
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div class="preview-formula-note">
                    <i class="ri-information-line"></i>
                    第一段：{{ previewAmount || 0 }} ÷ {{ previewBaseAmount }} = {{ previewRawUnits }}，{{ roundingMethodLabel }}得 <strong>{{ previewUnits }}</strong> 單位<br/>
                    第二段：單位數 × 各等級回饋率，{{ roundingMethodLabel }}得點數
                  </div>
                </div>
              </div>
            </div>

            <div class="form-section">
              <h2 class="section-title">變更備註（選填）</h2>
              <div class="form-group">
                <label class="form-label">備註說明</label>
                <textarea
                  v-model="form.remark"
                  class="form-control"
                  rows="3"
                  placeholder="請輸入此次變更的備註說明（選填）"
                  maxlength="500"
                ></textarea>
                <small class="form-text">此備註將記錄在變更歷史中，方便日後追蹤變更原因（最多 500 字）</small>
              </div>
            </div>

            <div v-if="rule && rule.updatedAt" class="form-section">
              <h2 class="section-title">變更記錄</h2>
              <div class="info-row">
                <span class="info-label">最後更新時間：</span>
                <span class="info-value">{{ formatDateTime(rule.updatedAt) }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">最後更新人員：</span>
                <span class="info-value">{{ rule.updatedByName || '系統' }}</span>
              </div>
              <div class="info-row">
                <button type="button" class="btn btn-sm btn-info" @click="handleViewLogs">
                  <i class="ri-file-list-line"></i>
                  查看變更歷史
                </button>
              </div>
            </div>

          </div>

          <div v-show="activeTab === 'settings'">
            <div class="form-section">
              <TimeRangeCalcSettingsPanel
                v-model:use-normal-calculation-settings="form.useNormalCalculationSettings"
                :custom-settings="form"
                :normal-rule="normalRule"
                :normal-rule-loading="normalRuleLoading"
                :errors="errors"
              />
            </div>

            <div class="form-section">
              <h2 class="section-title">變更備註（選填）</h2>
              <div class="form-group">
                <label class="form-label">備註說明</label>
                <textarea
                  v-model="form.remark"
                  class="form-control"
                  rows="3"
                  placeholder="請輸入此次變更的備註說明（選填）"
                  maxlength="500"
                ></textarea>
                <small class="form-text">此備註將記錄在變更歷史中，方便日後追蹤變更原因（最多 500 字）</small>
              </div>
            </div>

            <div v-if="rule && rule.updatedAt" class="form-section">
              <h2 class="section-title">變更記錄</h2>
              <div class="info-row">
                <span class="info-label">最後更新時間：</span>
                <span class="info-value">{{ formatDateTime(rule.updatedAt) }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">最後更新人員：</span>
                <span class="info-value">{{ rule.updatedByName || '系統' }}</span>
              </div>
              <div class="info-row">
                <button type="button" class="btn btn-sm btn-info" @click="handleViewLogs">
                  <i class="ri-file-list-line"></i>
                  查看變更歷史
                </button>
              </div>
            </div>

          </div>
        </form>
      </div>
    </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, reactive, computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { pointsEarningRulesService } from '@/api/modules/pointsEarningRules.js';
import { memberTierService } from '@/api/modules/memberTiers.js';
import { showSuccess, showError } from '@/utils/swal.js';
import { toDatetimeLocal, fromDatetimeLocal } from '@/utils/datetimeLocal.js';
import TimeRangeCalcSettingsPanel from './TimeRangeCalcSettingsPanel.vue';

export default {
  name: 'PointsEarningRulesTimeRangeEdit',
  components: {
    TimeRangeCalcSettingsPanel
  },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const { pageTitle } = usePageTitle('編輯時間區間給點規則');
    const activeTab = ref('tiers');
    const showHelp = ref(false);
    const loading = ref(false);
    const submitting = ref(false);
    const error = ref(null);
    const rule = ref(null);
    const normalRule = ref(null);
    const normalRuleLoading = ref(false);
    const tiers = ref([]);
    const timeOverlapWarning = ref('');
    const previewAmount = ref(100);

    const form = reactive({
      ruleName: '',
      startTime: '',
      endTime: '',
      tierConfig: {},
      useNormalCalculationSettings: true,
      calculationBase: 'paid_amount',
      roundingMethod: 'floor',
      baseAmount: 1,
      effectiveDays: 0,
      status: 'draft',
      remark: ''
    });

    const errors = reactive({});

    const fetchTiers = async () => {
      try {
        const response = await memberTierService.getTiers({ isActive: true });
        if (response && response.success && response.data) {
          tiers.value = response.data;
        }
      } catch (err) {
        console.error('獲取會員等級列表失敗:', err);
      }
    };

    const fetchNormalRule = async () => {
      normalRuleLoading.value = true;
      try {
        const response = await pointsEarningRulesService.getNormalRule();
        if (response && response.success && response.data) {
          normalRule.value = response.data;
        }
      } catch (err) {
        console.error('獲取常態規則失敗:', err);
      } finally {
        normalRuleLoading.value = false;
      }
    };

    const applyFormFromRule = (ruleData) => {
      form.ruleName = ruleData.ruleName || '';
      form.startTime = toDatetimeLocal(ruleData.startTime);
      form.endTime = toDatetimeLocal(ruleData.endTime);
      form.tierConfig = { ...ruleData.tierConfig };
      form.useNormalCalculationSettings = ruleData.useNormalCalculationSettings ?? true;
      form.calculationBase = ruleData.calculationBase || 'paid_amount';
      form.roundingMethod = ruleData.roundingMethod || 'floor';
      form.baseAmount = ruleData.baseAmount || 1;
      form.effectiveDays = ruleData.effectiveDays || 0;
      form.status = ruleData.status || 'draft';
      form.remark = '';
    };

    const fetchRule = async () => {
      loading.value = true;
      error.value = null;

      try {
        const response = await pointsEarningRulesService.getTimeRangeRule(route.params.id);

        if (response && response.success && response.data) {
          rule.value = response.data;
          applyFormFromRule(response.data);
        } else {
          error.value = response?.message || '獲取規則失敗';
        }
      } catch (err) {
        console.error('獲取規則失敗:', err);
        error.value = err.message || '獲取規則失敗';
        await showError('載入失敗', error.value);
      } finally {
        loading.value = false;
      }
    };

    const handleSubmit = async () => {
      Object.keys(errors).forEach(key => delete errors[key]);
      timeOverlapWarning.value = '';

      let hasError = false;

      if (!form.ruleName || form.ruleName.trim().length === 0) {
        errors.ruleName = '規則名稱不能為空';
        hasError = true;
      }

      if (!form.startTime) {
        errors.startTime = '開始時間不能為空';
        hasError = true;
      }

      if (!form.endTime) {
        errors.endTime = '結束時間不能為空';
        hasError = true;
      }

      if (form.startTime && form.endTime) {
        const start = new Date(form.startTime);
        const end = new Date(form.endTime);
        if (start >= end) {
          errors.endTime = '結束時間必須晚於開始時間';
          hasError = true;
        }
      }

      for (const [tierCode, rate] of Object.entries(form.tierConfig)) {
        if (rate === null || rate === undefined || rate === '') {
          continue;
        }
        if (!Number.isInteger(rate) || rate < 0) {
          errors[`tier_${tierCode}`] = '回饋率須為非負整數';
          hasError = true;
        }
      }

      if (!form.useNormalCalculationSettings) {
        if (!Number.isInteger(form.baseAmount) || form.baseAmount < 1) {
          errors.baseAmount = '金額計算基數必須為正整數';
          hasError = true;
        }
        if (!Number.isInteger(form.effectiveDays) || form.effectiveDays < 0) {
          errors.effectiveDays = '生效天數不可為負數';
          hasError = true;
        }
      }

      if (hasError) {
        return;
      }

      submitting.value = true;

      try {
        const tierConfig = {};
        for (const [tierCode, rate] of Object.entries(form.tierConfig)) {
          if (rate !== null && rate !== undefined) {
            tierConfig[tierCode] = rate;
          }
        }

        const startTimeISO = fromDatetimeLocal(form.startTime);
        const endTimeISO = fromDatetimeLocal(form.endTime);

        const payload = {
          ruleName: form.ruleName.trim(),
          startTime: startTimeISO,
          endTime: endTimeISO,
          tierConfig,
          useNormalCalculationSettings: form.useNormalCalculationSettings,
          status: form.status,
          remark: form.remark || undefined
        };

        if (!form.useNormalCalculationSettings) {
          payload.calculationBase = form.calculationBase;
          payload.roundingMethod = form.roundingMethod;
          payload.baseAmount = form.baseAmount;
          payload.effectiveDays = form.effectiveDays;
        }

        const response = await pointsEarningRulesService.updateTimeRangeRule(route.params.id, payload);

        if (response && response.success) {
          await showSuccess('設定儲存成功', '時間區間給點規則已更新');
          form.remark = '';
          await fetchRule();
        } else {
          throw new Error(response?.message || '更新失敗');
        }
      } catch (err) {
        console.error('更新規則失敗:', err);
        const errorData = err.data || err.response?.data || {};
        const errorMessage = errorData.message || err.message || '更新規則時發生錯誤';
        const errorObj = errorData.error || {};

        if (errorObj.code === 'RULE_VALIDATION_001' && errorObj.details?.overlaps) {
          const overlaps = errorObj.details.overlaps;
          const overlapNames = overlaps.map(o => o.ruleName || o.rule_name || '未知規則').join('、');
          timeOverlapWarning.value = `時間區間與以下規則重疊：${overlapNames}`;
          await showError('更新失敗', `時間區間與其他啟用中的規則重疊：${overlapNames}`);
        } else {
          await showError('儲存失敗', errorMessage);
        }
      } finally {
        submitting.value = false;
      }
    };

    const handleReset = () => {
      if (rule.value) {
        applyFormFromRule(rule.value);
      }
      Object.keys(errors).forEach(key => delete errors[key]);
      timeOverlapWarning.value = '';
    };

    const handleCancel = () => {
      router.push('/client/points/earning-rules/time-range');
    };

    const handleViewLogs = () => {
      router.push(`/client/points/earning-rules/time-range/${route.params.id}/logs`);
    };

    const formatDateTime = (dateTime) => {
      if (!dateTime) return '-';
      const date = new Date(dateTime);
      return date.toLocaleString('zh-TW', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    };

    const previewBaseAmount = computed(() => {
      if (form.useNormalCalculationSettings) {
        return normalRule.value?.baseAmount || 1;
      }
      return form.baseAmount || 1;
    });

    const effectiveRoundingMethod = computed(() => {
      if (form.useNormalCalculationSettings) {
        return normalRule.value?.roundingMethod || 'floor';
      }
      return form.roundingMethod || 'floor';
    });

    const effectiveDaysDisplay = computed(() => {
      if (form.useNormalCalculationSettings) {
        return normalRule.value?.effectiveDays || 0;
      }
      return form.effectiveDays || 0;
    });

    const roundingMethodLabel = computed(() => {
      const map = { round: '四捨五入', ceil: '無條件進位', floor: '無條件捨去' };
      return map[effectiveRoundingMethod.value] || '無條件捨去';
    });

    const applyRoundingPreview = (value) => {
      if (!isFinite(value) || isNaN(value)) return 0;
      switch (effectiveRoundingMethod.value) {
        case 'ceil': return Math.ceil(value);
        case 'round': return Math.round(value);
        case 'floor':
        default: return Math.floor(value);
      }
    };

    const getEffectiveTierRate = (tierCode) => {
      const raw = form.tierConfig[tierCode];
      if (raw !== null && raw !== undefined && raw !== '') {
        return raw;
      }
      return normalRule.value?.tierConfig?.[tierCode] ?? 0;
    };

    const displayTierRate = (tierCode) => {
      const raw = form.tierConfig[tierCode];
      if (raw === null || raw === undefined || raw === '') {
        const normalRate = normalRule.value?.tierConfig?.[tierCode];
        if (normalRate !== null && normalRate !== undefined) {
          return `沿用常態（${normalRate}%）`;
        }
        return '沿用常態規則';
      }
      return `${raw}%`;
    };

    const previewRawUnits = computed(() => {
      const base = previewBaseAmount.value;
      const amount = (previewAmount.value > 0 && isFinite(previewAmount.value)) ? previewAmount.value : 0;
      return (amount / base).toFixed(2);
    });

    const previewUnits = computed(() => {
      const base = previewBaseAmount.value;
      const amount = (previewAmount.value > 0 && isFinite(previewAmount.value)) ? previewAmount.value : 0;
      return applyRoundingPreview(amount / base);
    });

    const tierPreview = (tierCode) => {
      const rate = getEffectiveTierRate(tierCode);
      const units = previewUnits.value;
      const points = applyRoundingPreview(units * (rate / 100));
      return { units, points: Math.max(0, points) };
    };

    onMounted(async () => {
      await Promise.all([fetchTiers(), fetchNormalRule(), fetchRule()]);
    });

    return {
      pageTitle,
      activeTab,
      showHelp,
      loading,
      submitting,
      error,
      rule,
      normalRule,
      normalRuleLoading,
      tiers,
      form,
      errors,
      timeOverlapWarning,
      previewAmount,
      previewBaseAmount,
      effectiveDaysDisplay,
      handleSubmit,
      handleReset,
      handleCancel,
      handleViewLogs,
      fetchRule,
      formatDateTime,
      roundingMethodLabel,
      displayTierRate,
      previewRawUnits,
      previewUnits,
      tierPreview
    };
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.basic-info-section {
  margin-bottom: 24px;
  padding-bottom: 24px;
  border-bottom: 1px solid #e0e0e0;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.required::after {
  content: ' *';
  color: #dc3545;
}

/* 頁籤 */
.tab-bar {
  display: flex;
  gap: 4px;
  border-bottom: 2px solid #e0e0e0;
  margin-bottom: 24px;
}

.tab-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 20px;
  border: none;
  background: none;
  color: #666;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  border-radius: 4px 4px 0 0;
  transition: color 0.15s, border-color 0.15s, background 0.15s;
}

.tab-btn:hover {
  color: #4a90d9;
  background: #f5f8ff;
}

.tab-btn.active {
  color: #4a90d9;
  border-bottom-color: #4a90d9;
  background: #f5f8ff;
}

.quick-rule-summary {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  background: #f8faff;
  border: 1px solid #d8e4f8;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 16px;
}

.quick-rule-summary-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 600;
  color: #2a5bd7;
}

.quick-rule-summary-items {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  color: #334155;
  font-size: 13px;
  flex: 1;
}

.effective-mode-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  font-weight: 600;
}

.effective-mode-immediate {
  background: #dcfce7;
  color: #166534;
}

.effective-mode-delayed {
  background: #ffedd5;
  color: #9a3412;
}

.calc-mode-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  font-weight: 600;
  font-size: 12px;
}

.mode-normal {
  background: #e0f2fe;
  color: #0369a1;
}

.mode-custom {
  background: #fef3c7;
  color: #b45309;
}

.tiers-layout {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 32px;
  align-items: start;
}

.sub-section-title {
  font-size: 15px;
  font-weight: 600;
  color: #444;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e8e8e8;
}

.preview-amount-row {
  margin-bottom: 16px;
}

.preview-amount-label {
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: #555;
  margin-bottom: 6px;
}

.preview-amount-input {
  max-width: 200px;
}

.preview-settings-hint {
  display: block;
  margin-top: 6px;
  font-size: 12px;
  color: #888;
}

.preview-table-wrap {
  overflow-x: auto;
  margin-bottom: 12px;
}

.preview-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.preview-table th {
  background: #f5f7fa;
  padding: 8px 12px;
  text-align: left;
  font-weight: 600;
  color: #555;
  border-bottom: 2px solid #e0e0e0;
}

.preview-table td {
  padding: 10px 12px;
  border-bottom: 1px solid #f0f0f0;
  color: #333;
}

.preview-tier-name {
  font-weight: 600;
}

.preview-units {
  color: #666;
}

.preview-points strong {
  font-size: 16px;
  color: #4a90d9;
}

.preview-formula-note {
  font-size: 12px;
  color: #777;
  background: #f8f9fa;
  border-radius: 6px;
  padding: 10px 14px;
  line-height: 1.8;
}

.preview-formula-note i {
  margin-right: 4px;
  color: #4a90d9;
}

.calc-preview-box {
  background: #f0f4ff;
  border: 1px solid #c8d8f8;
  border-radius: 8px;
  padding: 16px 20px;
  margin-bottom: 24px;
}

.calc-preview-title {
  font-size: 13px;
  font-weight: 600;
  color: #4a6fa5;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.calc-preview-steps {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 12px;
}

.calc-step {
  display: flex;
  align-items: center;
  gap: 10px;
}

.calc-step-num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  background: #4a90d9;
  color: #fff;
  border-radius: 50%;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
}

.calc-step-text {
  font-size: 13px;
  color: #333;
}

.calc-arrow {
  margin: 0 4px;
  color: #4a90d9;
  font-weight: 600;
}

.calc-example {
  font-size: 12px;
  color: #555;
  background: #fff;
  border-radius: 6px;
  padding: 8px 12px;
  border: 1px solid #dce8fa;
  line-height: 1.7;
}

.base-amount-row {
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-width: 420px;
}

.base-amount-presets {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.base-amount-btn {
  padding: 6px 14px;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  background: #fff;
  color: #495057;
  font-size: 14px;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
}

.base-amount-btn:hover {
  background: #f0f4ff;
  border-color: #4a90d9;
}

.base-amount-btn.active {
  background: #4a90d9;
  border-color: #4a90d9;
  color: #fff;
  font-weight: 600;
}

.base-amount-custom {
  display: flex;
  align-items: center;
  gap: 8px;
  max-width: 180px;
}

.input-suffix {
  white-space: nowrap;
  color: #666;
  font-size: 14px;
}

.help-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  margin-left: 8px;
  border: 1px solid #aac8f0;
  border-radius: 50%;
  background: #e8f4fd;
  color: #1a73e8;
  font-size: 14px;
  cursor: pointer;
  vertical-align: middle;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
  line-height: 1;
}

.help-icon-btn:hover,
.help-icon-btn.active {
  background: #1a73e8;
  border-color: #1a73e8;
  color: #fff;
}

.setup-guide-alert {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 18px;
  background: #e8f4fd;
  border: 1px solid #b8daff;
  border-radius: 8px;
  margin-bottom: 20px;
  color: #004085;
}

.setup-guide-alert > i {
  font-size: 20px;
  flex-shrink: 0;
  margin-top: 2px;
  color: #1a73e8;
}

.setup-guide-alert > div {
  flex: 1;
}

.setup-guide-alert strong {
  display: block;
  font-size: 14px;
  margin-bottom: 6px;
}

.setup-guide-alert ol {
  font-size: 13px;
  margin: 0;
  padding-left: 18px;
  color: #1a4a7a;
  line-height: 1.8;
}

.guide-close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  color: #6b9bd2;
  font-size: 18px;
  cursor: pointer;
  border-radius: 4px;
  transition: background 0.15s, color 0.15s;
}

.guide-close-btn:hover {
  background: #c8dff8;
  color: #004085;
}

.guide-fade-enter-active,
.guide-fade-leave-active {
  transition: opacity 0.2s, transform 0.2s;
}

.guide-fade-enter-from,
.guide-fade-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

.tier-config-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.tier-config-item {
  display: flex;
  align-items: flex-start;
  gap: 20px;
  padding: 16px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #f9f9f9;
}

.tier-info {
  flex: 0 0 200px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.tier-name {
  font-weight: 600;
  font-size: 16px;
}

.tier-code {
  font-size: 12px;
  color: #666;
  background: #e0e0e0;
  padding: 2px 6px;
  border-radius: 4px;
}

.tier-input {
  flex: 1;
}

.input-group {
  max-width: 200px;
}

.form-section {
  margin-bottom: 32px;
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 16px;
  color: #333;
}

.section-description {
  color: #666;
  margin-bottom: 20px;
  font-size: 14px;
}

.form-actions {
  display: flex;
  gap: 12px;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid #e0e0e0;
}

.info-row {
  display: flex;
  gap: 12px;
  margin-bottom: 8px;
}

.info-label {
  font-weight: 500;
  color: #666;
  min-width: 120px;
}

.info-value {
  color: #333;
}

@media (max-width: 768px) {
  .form-row {
    grid-template-columns: 1fr;
  }

  .tiers-layout {
    grid-template-columns: 1fr;
  }

  .tier-config-item {
    flex-direction: column;
  }

  .tier-info {
    flex: 1;
  }
}
</style>
